import React, { useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import { EyeCloseIcon, EyeIcon } from "../../icons";
import Checkbox from "../../components/form/input/Checkbox";
import PageMeta from "../../components/common/PageMeta";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "../../components/form/Select";
import { useNavigate } from "react-router-dom";
import { FormInputEvent } from '../../types/form';

export default function SignUp() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    mobilePhone: "",
    isDeleted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  

  // Handle Input Change
  const handleChange = (e: FormInputEvent) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle Select Change
  const handleSelectChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle Form Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isChecked) {
      setError("You must agree to the Terms and Conditions.");
      return;
    }

    setLoading(true);
    setError("");

    // Construct payload for /api/admins
    const payload = {
      fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      username: formData.username,
      email: formData.email,
      phoneNumber: formData.mobilePhone,
      passwordHash: formData.password, // If backend expects plain password, send as is
      isActive: true,
      isSuperAdmin: true,
      isDeleted: formData.isDeleted,
    };

    try {
      const response = await axiosInstance.post("/admins", payload);
      console.log("Registration Successful:", response.data);
      alert("Registration successful!");
      navigate("/signin");
    } catch (err: any) {
      setError(err.response?.data?.message || "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Select Options
  const salutationOptions = [
    { value: "Mr.", label: "Mr." },
    { value: "Ms.", label: "Ms." },
    { value: "Dr.", label: "Dr." },
    { value: "Prof.", label: "Prof." },
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];

  return (
    <>
      <PageMeta title="Sign Up" description="Create an admin account for Taxi Admin." />
      <div className="flex w-full h-screen bg-white dark:bg-gray-900">
        <div className="flex flex-col flex-1 p-6 sm:p-8">
          <div className="w-full max-w-3xl mx-auto">
            <h1 className="text-xl font-semibold text-gray-800 dark:text-white">Sign Up</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your details to create an account.
            </p>
            {error && <p className="text-sm text-red-500">{error}</p>}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* First Name */}
              <div>
                <Label>First Name*</Label>
                <Input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John"  />
              </div>

              {/* Last Name */}
              <div>
                <Label>Last Name*</Label>
                <Input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe"  />
              </div>

              {/* Username */}
              <div>
                <Label>Username*</Label>
                <Input type="text" name="username" value={formData.username} onChange={handleChange} placeholder="username" />
              </div>

              {/* Email */}
              <div>
                <Label>Email*</Label>
                <Input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="example@email.com"  />
              </div>

              {/* Password */}
              <div>
                <Label>Password*</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} placeholder="Enter password"  />
                  <span onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 cursor-pointer -translate-y-1/2">
                    {showPassword ? <EyeIcon className="fill-gray-500" /> : <EyeCloseIcon className="fill-gray-500" />}
                  </span>
                </div>
              </div>

              {/* Mobile Phone */}
              <div>
                <Label>Mobile Phone</Label>
                <Input type="tel" name="mobilePhone" value={formData.mobilePhone} onChange={handleChange} placeholder="+92 300 1234567" />
              </div>

              {/* Mark as Deleted (for testing only) */}
              <div className="flex items-center gap-3 col-span-3">
                <Checkbox className="w-5 h-5" checked={formData.isDeleted} onChange={(checked: boolean) => setFormData(prev => ({ ...prev, isDeleted: checked }))} />
                <p className="text-sm text-gray-500">Mark as Deleted (for testing only)</p>
              </div>

              {/* Terms & Conditions */}
              <div className="flex items-center gap-3 col-span-3">
                <Checkbox className="w-5 h-5" checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
                <p className="text-sm text-gray-500">
                  I agree to the <span className="text-blue-500">Terms and Conditions</span> and{" "}
                  <span className="text-blue-500">Privacy Policy</span>.
                </p>
              </div>

              {/* Submit Button */}
              <div className="col-span-3">
                <button type="submit" className="w-full px-4 py-3 text-white bg-blue-500 rounded-lg hover:bg-blue-600" disabled={loading}>
                  {loading ? "Signing Up..." : "Sign Up"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
